import { RCU_GROUP, moduleIdOfRail } from '../catalog/protocol';
import { RAIL_TYPE_SPECS } from '../catalog/railType';
import type { RcuConfig } from '../model';
import type { Result } from '../result';
import type { RcuDocument } from '../wire';
import { readAllDin, readAllRelays } from './commands';

/**
 * The snapshot taken once when the link comes up.
 *
 * MQTT has no state topic: the retained status document describes the device,
 * not its channels, and after that the RCU only speaks when something moves or
 * when it is asked. So a screen that shows channel state starts from one round
 * of reads — the relay bank and one per configured module — and then follows the
 * device's own events. It is deliberately not a poll: nothing here repeats on a
 * timer, and a channel that has never been reported stays unknown rather than
 * being drawn as off.
 *
 * DALI bridges are left out on purpose: the firmware does not route a bridge's
 * DIN response (`5c` is not among the recognised types), so asking would add a
 * command that can only go unanswered.
 */
export interface HydrationRead {
  readonly document: Result<RcuDocument>;
  /** What the read is for, in the same words the console's own buttons use. */
  readonly summary: string;
}

export const hydrationReads = (config: RcuConfig): readonly HydrationRead[] => [
  { document: readAllRelays(), summary: 'Relays 1–26 read' },
  ...config.rails.map((rail) => ({
    document: readAllDin({
      group: RCU_GROUP.DEFAULT,
      module: moduleIdOfRail(rail.type),
      addr: rail.address,
    }),
    summary: `${RAIL_TYPE_SPECS[rail.type].shortLabel} ${rail.address} read`,
  })),
];
